import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormControlLabel,
    IconButton,
    Chip,
    Skeleton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    Snackbar,
    Divider,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Delivery, RouteDeliveryGroup } from 'typings/models';
import {
    getAdminCompletedGrouped,
    toggleDeliveryReview,
    deleteDelivery,
    getDeliveryFileUrls,
} from 'api/deliveries';
import { formatDate } from 'misc/dateUtils';

const CompletedDeliveriesPage = () => {
    const [groups, setGroups] = useState<RouteDeliveryGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [filesDialogOpen, setFilesDialogOpen] = useState(false);
    const [fileUrls, setFileUrls] = useState<{ url: string; filename: string; content_type: string; file_type: string | null }[]>([]);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false, message: '', severity: 'success',
    });

    useEffect(() => {
        getAdminCompletedGrouped().then((data) => {
            setGroups(data);
            setLoading(false);
        });
    }, []);

    const updateDelivery = (updated: Delivery) => {
        setGroups((prev) =>
            prev.map((g) => ({
                ...g,
                deliveries: g.deliveries.map((d) => (d.id === updated.id ? updated : d)),
            }))
        );
    };

    const removeDelivery = (deliveryId: string) => {
        setGroups((prev) =>
            prev
                .map((g) => ({ ...g, deliveries: g.deliveries.filter((d) => d.id !== deliveryId) }))
                .filter((g) => g.deliveries.length > 0)
        );
    };

    const handleToggleReview = async (delivery: Delivery) => {
        const updated = await toggleDeliveryReview(delivery.id);
        if (updated) updateDelivery(updated);
    };

    const handleDelete = async (deliveryId: string) => {
        const ok = await deleteDelivery(deliveryId);
        if (ok) {
            removeDelivery(deliveryId);
            setSnackbar({ open: true, message: 'Entrega eliminada', severity: 'success' });
        } else {
            setSnackbar({ open: true, message: 'Error al eliminar la entrega', severity: 'error' });
        }
    };

    const handleViewFiles = async (deliveryId: string) => {
        setLoadingFiles(true);
        setFilesDialogOpen(true);
        const urls = await getDeliveryFileUrls(deliveryId);
        setFileUrls(urls);
        setLoadingFiles(false);
    };

    const isImage = (contentType: string | null) => contentType?.startsWith('image/') ?? false;

    if (loading) {
        return <Box p={2}><Skeleton variant="rectangular" height={200} /></Box>;
    }

    return (
        <Box p={2}>
            <Typography variant="h6" gutterBottom>
                Entregas completadas (últimos 15 días)
            </Typography>

            {groups.length === 0 && (
                <Typography color="text.secondary">No hay entregas completadas en los últimos 15 días.</Typography>
            )}

            {groups.map((group, gi) => {
                const allReviewed = group.deliveries.every((d) => d.reviewed);
                return (
                    <Accordion key={group.route_id ?? gi} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                                <Box>
                                    <Typography fontWeight="bold">
                                        {group.driver_name ?? '—'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {group.route_name ?? group.route_id ?? '—'} · {formatDate(group.scheduled_date)}
                                    </Typography>
                                </Box>
                                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                        label={`${group.deliveries.length} orden${group.deliveries.length !== 1 ? 'es' : ''}`}
                                        size="small"
                                        variant="outlined"
                                    />
                                    {allReviewed && <Chip label="Revisado" color="success" size="small" />}
                                </Box>
                            </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ p: 0 }}>
                            {group.deliveries.map((delivery, di) => (
                                <Box key={delivery.id}>
                                    {di > 0 && <Divider />}
                                    <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {delivery.order.id}
                                                </Typography>
                                                {delivery.status === 'Failed' && (
                                                    <Chip label="Problema" color="error" size="small" />
                                                )}
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                {delivery.order.client.name} · {delivery.order.client.address}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {delivery.delivery_files?.filter(f => f.file_type !== 'problem').length ?? 0} comprobante(s) ·{' '}
                                        {delivery.delivery_files?.filter(f => f.file_type === 'problem').length ?? 0} evidencia(s)
                                            </Typography>
                                        </Box>

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={!!delivery.reviewed}
                                                    onChange={() => handleToggleReview(delivery)}
                                                    color="success"
                                                    size="small"
                                                />
                                            }
                                            label={<Typography variant="body2">Revisado</Typography>}
                                        />

                                        <Button
                                            size="small"
                                            startIcon={<AttachFileIcon />}
                                            onClick={() => handleViewFiles(delivery.id)}
                                            disabled={(delivery.delivery_files?.length ?? 0) === 0}
                                        >
                                            Archivos
                                        </Button>

                                        <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleDelete(delivery.id)}
                                            title="Eliminar entrega"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))}
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            <Dialog open={filesDialogOpen} onClose={() => setFilesDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Archivos adjuntos</DialogTitle>
                <DialogContent>
                    {loadingFiles ? (
                        <Skeleton variant="rectangular" height={100} />
                    ) : fileUrls.length === 0 ? (
                        <Typography>No hay archivos adjuntos.</Typography>
                    ) : (
                        <List>
                            {fileUrls.map((f, i) => (
                                <ListItem key={i} disablePadding sx={{ mb: 1 }}>
                                    <Box sx={{ width: '100%' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Chip
                                                label={f.file_type === 'problem' ? 'Evidencia problema' : 'Comprobante'}
                                                color={f.file_type === 'problem' ? 'error' : 'success'}
                                                size="small"
                                            />
                                        </Box>
                                        {isImage(f.content_type) ? (
                                            <Box component="a" href={f.url} target="_blank" rel="noopener noreferrer">
                                                <Box
                                                    component="img"
                                                    src={f.url}
                                                    alt={f.filename}
                                                    sx={{ maxWidth: '100%', maxHeight: 300, borderRadius: 1, display: 'block' }}
                                                />
                                                <Typography variant="caption">{f.filename}</Typography>
                                            </Box>
                                        ) : (
                                            <Button
                                                component="a"
                                                href={f.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                startIcon={<AttachFileIcon />}
                                                variant="outlined"
                                                size="small"
                                            >
                                                {f.filename}
                                            </Button>
                                        )}
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFilesDialogOpen(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CompletedDeliveriesPage;
